import { Column } from "react-data-grid";
import {
  CustomerPortalCodebooksResult,
  CustomerPortalCodebookType,
  TableQuery,
  TableSortQuery,
} from "../common/models";
import { SelectItem } from "../components/select/Select";
import { FilterItem } from "../components/filter/models";
import { TFunction } from "i18next";
import { getI18n } from "react-i18next";
import { compareStrings, compareStringsDesc } from "./compareHelpers";

export const transformTableQueryForKy = (
  queryParams: unknown,
): Record<string, string | number | boolean> => {
  const query = queryParams as Record<
    string,
    string | number | boolean | undefined | null
  >;
  const result: Record<string, string | number | boolean> = {};

  Object.keys(query).map((key) => {
    if (query[key]) {
      result[key] = query[key];
    }
  });

  return result;
};

export const getParamsAsKeys = (params: unknown) => {
  const typedParams = params as Record<string, string>;
  return Object.keys(typedParams).map((key) => `${key}_${typedParams[key]}`);
};

export const getTableQueryKey = (name: string, params: TableQuery) => [
  name,
  ...getParamsAsKeys(params),
];

export const getTablePageCount = (total: number, pageSize: number) =>
  Math.ceil(total / pageSize);

export const getTableDataByPage = <T>(query: TableQuery, data: T[]): T[] => {
  return data.slice(
    query.pageSize * (query.page - 1),
    query.pageSize * query.page,
  );
};

export const setTableLastColumnMaxWidth = <T>(columns: Column<T>[]) => {
  const lastItem = columns.pop();

  if (lastItem) columns.push({ ...lastItem, width: "auto" });

  return columns;
};

export const getTableFiltersFromCodebooks = (
  requestedFilters: { key: string; source: CustomerPortalCodebookType }[],
  t: TFunction,
  codebooks?: CustomerPortalCodebooksResult,
): FilterItem[] => {
  const filters: FilterItem[] = [];

  requestedFilters.forEach((filter) => {
    const codebook = codebooks?.[filter.source];

    if (codebook) {
      filters.push({
        key: filter.key,
        label: t(filter.source),
        options: codebook.map((item) => ({
          value: item.id,
          label: item.name,
        })),
      });
    }
  });

  return filters;
};

export const getTableParsedFiltersForQuery = (
  selectedFilters: Record<string, SelectItem[]>,
) => {
  return Object.keys(selectedFilters).reduce(
    (acc: Record<string, string>, key) => {
      const selectedValues = selectedFilters[key].map((item) => item.value);

      acc[key] = selectedValues.join(",");

      return acc;
    },
    {},
  );
};

export const renderServiceObjects = (serviceObjectNames: string[]) => {
  const { t } = getI18n();

  if (serviceObjectNames.length === 0) {
    return "-";
  }

  if (serviceObjectNames.length < 3) {
    return serviceObjectNames.join(", ");
  }

  const visibleServiceObjectNames = serviceObjectNames.slice(0, 2).join(", ");

  return `${visibleServiceObjectNames} (+ ${t("more_count", { count: serviceObjectNames.length - 2 })})`;
};

const getComparableValue = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  return value as string | null | undefined;
};

export function getSortedTableData<T extends object>(
  data: T[],
  query: TableSortQuery,
): T[];
export function getSortedTableData(
  data: object[],
  query: TableSortQuery,
): object[] {
  if (!query.sortBy || !query.sortOrder) {
    return data;
  }

  const sortedItems = [...data];

  sortedItems.sort((first, second) => {
    const firstValue = getComparableValue(
      (first as Record<string, unknown>)[query.sortBy!],
    );
    const secondValue = getComparableValue(
      (second as Record<string, unknown>)[query.sortBy!],
    );

    if (typeof firstValue === "number" && typeof secondValue === "number") {
      return query.sortOrder === "ascend"
        ? firstValue - secondValue
        : secondValue - firstValue;
    }

    return query.sortOrder === "ascend"
      ? compareStrings(firstValue?.toString(), secondValue?.toString())
      : compareStringsDesc(firstValue?.toString(), secondValue?.toString());
  });

  return sortedItems;
}

export const defaultTableQuery: TableQuery = {
  page: 1,
  pageSize: 10,
};

export const defaultSelectQuery: TableQuery = {
  page: 1,
  pageSize: 300,
};
